<template>
  <section
    v-show="
      promopageData && promopageData.pages && promopageData.pages.length > 0
    "
    class="cs-promo-page"
  >
    <h2 v-if="promopageData && promopageData.title">
      {{ promopageData.title }}
    </h2>
    <ul
      v-if="
        promopageData && promopageData.pages && promopageData.pages.length > 0
      "
    >
      <li v-for="page of promopageData.pages || []" :key="page.url">
        <pu-link :path="page.url">
          {{ page.title }}
        </pu-link>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { useRoute } from 'nuxt/app'
import { logger } from '../../../utility/logger'
import { getPromoPagesMachine } from './inversify.config'
import { PromoPages } from './schemas'

const props = defineProps<{ slug: string }>()

const route = useRoute()
const lang = (route.params.lang as string) || 'en-gb'
const isMounted = ref(true)

onUnmounted(() => {
  isMounted.value = false
})

const { data: promopageData } = await useAsyncData(
  `CampsiteDetailsPromoPages-${props.slug}-${lang}`,
  async () => {
    logger(`CampsiteDetailsPromoPages-${props.slug}-${lang}`)
    return await setupPromoPagesMachine(lang)
  },
  // this stops the double fetch from server and client
  {
    server: true,
    lazy: process.client,
    dedupe: 'cancel',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Nuxt payload data is untyped
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

function setupPromoPagesMachine(
  langCode: string,
): Promise<PromoPages | undefined> {
  return new Promise((resolve, reject) => {
    const machine = getPromoPagesMachine(langCode)
    machine
      .onTransition((state) => {
        if (!isMounted.value) return
        if (state.matches('results')) {
          resolve(state.context.data)
        }
        if (state.matches('error')) {
          reject(new Error('Promo pages machine error'))
        }
      })
      .start()
      .send('FETCH', { slug: props.slug })
    return machine
  })
}
</script>

<style scoped>
section {
  padding: 1.4em;
  border-bottom: 4px solid var(--colour-page-background);
  font-size: var(--body);
}

h2 {
  color: var(--colour-dark-blue);
  font-size: 1.15em;
  line-height: 1.25;
  margin: 0 0 0.25em 0;
  font-weight: normal;
}

a {
  color: var(--colour-dark-blue);
  padding: 0.9em 0;
  line-height: 1.2;
  display: block;
}

li {
  list-style: disc;
  margin: 0 0 0 1em;
  padding: 0;
}
</style>
